import React, { useState, useEffect } from 'react';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { setMyGitHubUsername, getMyTemporaryAccess, getMyExpiredAccess, requestAccessRenewal } from '../services/api';
import { formatAccessExpiry } from '../utils/formatAccessExpiry';
import './Settings.css';
import './AdminPages.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function permissionLabel(permission) {
  if (permission === 'pull' || permission === 'read') return 'Read';
  if (permission === 'push' || permission === 'write') return 'Write';
  if (permission === 'admin') return 'Admin';
  return permission || '';
}

const Settings = () => {
  const { user, loading: authLoading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [mfaSetup, setMfaSetup] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState(false);
  const [disableForm, setDisableForm] = useState({ password: '', token: '' });
  const [changePasswordForm, setChangePasswordForm] = useState({ currentPassword: '', newPassword: '', mfaToken: '' });
  const [githubUsername, setGithubUsername] = useState('');
  const [tempAccess, setTempAccess] = useState([]);
  const [expiredAccess, setExpiredAccess] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const expiredPages = usePagination(expiredAccess, 10, expiredAccess.length);
  const tempPages = usePagination(tempAccess, 10, tempAccess.length);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user?.githubUsername) setGithubUsername(user.githubUsername);
    if (user) {
      getMyTemporaryAccess()
        .then((res) => setTempAccess(res.data || []))
        .catch(() => {});
      getMyExpiredAccess()
        .then((res) => setExpiredAccess(res.data || []))
        .catch(() => {});
    }
  }, [user, authLoading, navigate]);

  const handleSaveGitHubUsername = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      await setMyGitHubUsername(githubUsername.trim());
      setSuccess('GitHub username saved. You can now request repository access.');
      await checkAuth();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save GitHub username');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupMFA = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.post(`${API_URL}/auth/mfa/setup`, {}, {
        withCredentials: true
      });
      setMfaSetup(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleEnableMFA = async (e) => {
    e.preventDefault();
    const token = e.target.token.value;
    
    try {
      setLoading(true);
      setError('');
      const response = await axios.post(`${API_URL}/auth/mfa/enable`, {
        token
      }, {
        withCredentials: true
      });
      
      setBackupCodes(response.data.backupCodes);
      setMfaSetup(null);
      setSuccess('MFA enabled successfully! Save your backup codes.');
      await checkAuth();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enable MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      await axios.post(`${API_URL}/auth/mfa/disable`, disableForm, {
        withCredentials: true
      });
      
      setSuccess('MFA disabled successfully');
      setShowDisableForm(false);
      setDisableForm({ password: '', token: '' });
      await checkAuth();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      await axios.post(`${API_URL}/auth/change-password`, changePasswordForm, {
        withCredentials: true
      });
      
      setSuccess('Password changed successfully');
      setShowChangePasswordForm(false);
      setChangePasswordForm({ currentPassword: '', newPassword: '', mfaToken: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings">
      <h1 className="page-title">Settings</h1>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">GitHub account for repository access</h2>
        </div>
        <p>
          Repository access is granted to a <strong>GitHub username</strong>.
          If you do not have a GitHub account yet, create one first, then save your username here (or connect GitHub OAuth).
        </p>
        <p style={{ marginBottom: '1rem' }}>
          <a href="https://github.com/signup" target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-sm">
            Create a GitHub account
          </a>
        </p>
        <form onSubmit={handleSaveGitHubUsername} className="form-row" style={{ alignItems: 'end' }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label className="form-label">Your GitHub username</label>
            <input
              className="form-input"
              value={githubUsername}
              onChange={(e) => setGithubUsername(e.target.value)}
              placeholder="your-github-handle"
              required
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving…' : 'Save'}
          </button>
        </form>
        {!user?.hasGitHubToken && (
          <p style={{ marginTop: '0.75rem' }}>
            Optional: <a href={`${API_URL}/auth/github`}>Connect GitHub OAuth</a> to link your account automatically,
            then go to <a href="/repositories">Repositories</a> to request access.
          </p>
        )}
        {user?.githubUsername && (
          <p style={{ marginTop: '0.75rem' }} className="form-hint">
            Mapped as <strong>@{user.githubUsername}</strong>. You can request repository access from Repositories.
          </p>
        )}
      </div>

      {expiredAccess.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Expired access</h2>
          </div>
          <p className="form-hint">This access was revoked. Request it again with a business justification.</p>
          <ul className="access-expiry-list">
            {expiredPages.pageItems.map((a) => (
              <li key={a._id} className="access-expiry-row">
                <div className="access-expiry-main">
                  <span className="access-expiry-repo">{a.repository?.fullName || 'Repository'}</span>
                  <span className="access-expiry-meta">Ended {formatAccessExpiry(a.expiresAt)}</span>
                </div>
                {a.repository?.owner && a.repository?.name && (
                  <Link
                    className="btn btn-sm btn-primary"
                    to={`/repositories/${a.repository.owner}/${a.repository.name}`}
                  >
                    Request again
                  </Link>
                )}
              </li>
            ))}
          </ul>
          <Pagination
            page={expiredPages.page}
            totalPages={expiredPages.totalPages}
            total={expiredPages.total}
            pageSize={expiredPages.pageSize}
            onPageChange={expiredPages.setPage}
            label="expired grants"
          />
        </div>
      )}

      {tempAccess.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Temporary access</h2>
          </div>
          <ul className="access-expiry-list">
            {tempPages.pageItems.map((a) => (
              <li key={a._id} className="access-expiry-row">
                <div className="access-expiry-main">
                  <span className="access-expiry-repo">{a.repository?.fullName || 'Repository'}</span>
                  <span className="access-expiry-meta">
                    {permissionLabel(a.permission)} · Expires {formatAccessExpiry(a.expiresAt)}
                    {a.renewalRequested ? ' · Renewal pending' : ''}
                  </span>
                </div>
                {!a.renewalRequested && (
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    onClick={async () => {
                      const reason = window.prompt('Why do you need a renewal?');
                      if (!reason) return;
                      try {
                        await requestAccessRenewal(a._id, reason);
                        setSuccess('Renewal requested');
                        const res = await getMyTemporaryAccess();
                        setTempAccess(res.data || []);
                      } catch (err) {
                        setError(err.response?.data?.error || 'Renewal failed');
                      }
                    }}
                  >
                    Request renewal
                  </button>
                )}
              </li>
            ))}
          </ul>
          <Pagination
            page={tempPages.page}
            totalPages={tempPages.totalPages}
            total={tempPages.total}
            pageSize={tempPages.pageSize}
            onPageChange={tempPages.setPage}
            label="grants"
          />
        </div>
      )}

      {user?.accountType === 'email' || user?.accountType === 'both' ? (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Change Password</h2>
          </div>
          {!showChangePasswordForm ? (
            <button
              className="btn btn-primary"
              onClick={() => setShowChangePasswordForm(true)}
            >
              Change Password
            </button>
          ) : (
            <form onSubmit={handleChangePassword} className="change-password-form">
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={changePasswordForm.currentPassword}
                  onChange={(e) => setChangePasswordForm({ ...changePasswordForm, currentPassword: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-input"
                  value={changePasswordForm.newPassword}
                  onChange={(e) => setChangePasswordForm({ ...changePasswordForm, newPassword: e.target.value })}
                  required
                  minLength={8}
                />
              </div>
              {user?.mfaEnabled && (
                <div className="form-group">
                  <label className="form-label">MFA Token</label>
                  <input
                    type="text"
                    className="form-input mfa-input"
                    value={changePasswordForm.mfaToken}
                    onChange={(e) => setChangePasswordForm({ ...changePasswordForm, mfaToken: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    maxLength={6}
                    placeholder="000000"
                  />
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowChangePasswordForm(false);
                    setChangePasswordForm({ currentPassword: '', newPassword: '', mfaToken: '' });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      ) : null}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Two-Factor Authentication (MFA)</h2>
        </div>

        {user?.mfaEnabled ? (
          <div>
            <div className="mfa-status enabled">
              <span className="status-icon">✓</span>
              <span>MFA is enabled</span>
            </div>
            
            {backupCodes && (
              <div className="backup-codes">
                <h3>Backup Codes (Save these securely!)</h3>
                <div className="codes-list">
                  {backupCodes.map((code, index) => (
                    <div key={index} className="backup-code">{code}</div>
                  ))}
                </div>
                <p className="codes-warning">
                  ⚠️ These codes will only be shown once. Save them in a secure location.
                </p>
              </div>
            )}

            {!showDisableForm ? (
              <button
                className="btn btn-danger"
                onClick={() => setShowDisableForm(true)}
              >
                Disable MFA
              </button>
            ) : (
              <form onSubmit={handleDisableMFA} className="disable-mfa-form">
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={disableForm.password}
                    onChange={(e) => setDisableForm({ ...disableForm, password: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">MFA Token</label>
                  <input
                    type="text"
                    className="form-input"
                    value={disableForm.token}
                    onChange={(e) => setDisableForm({ ...disableForm, token: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    required
                    maxLength={6}
                    placeholder="000000"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-danger" disabled={loading}>
                    {loading ? 'Disabling...' : 'Disable MFA'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDisableForm(false);
                      setDisableForm({ password: '', token: '' });
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <div>
            <div className="mfa-status disabled">
              <span className="status-icon">✗</span>
              <span>MFA is not enabled</span>
            </div>

            {!mfaSetup ? (
              <button
                className="btn btn-primary"
                onClick={handleSetupMFA}
                disabled={loading}
              >
                {loading ? 'Setting up...' : 'Enable MFA'}
              </button>
            ) : (
              <div className="mfa-setup">
                <h3>Scan QR Code</h3>
                <p>Use an authenticator app (Google Authenticator, Authy, etc.) to scan this QR code:</p>
                <div className="qr-code-container">
                  <img src={mfaSetup.qrCode} alt="MFA QR Code" />
                </div>
                <p className="manual-entry">
                  Or enter this key manually: <code>{mfaSetup.manualEntryKey}</code>
                </p>
                <form onSubmit={handleEnableMFA}>
                  <div className="form-group">
                    <label className="form-label">Enter 6-digit code to verify</label>
                    <input
                      type="text"
                      name="token"
                      className="form-input mfa-input"
                      required
                      maxLength={6}
                      placeholder="000000"
                      onChange={(e) => e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6)}
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Enabling...' : 'Enable MFA'}
                  </button>
                </form>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
